import { objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useMemo } from 'react';
import { useCountriesList, FormItem, FormLabel, FormInputText, PhoneTooltip } from '@tesla/design-system-react';
import { useFormApi, useField, utils } from 'informed';
import { FeedBack } from './Feedback.js';
import { getSupportedCountries, getCountryCode, phoneFormatter, getExample, getFormats, getPatterns } from '@tesla/intl-phone';

var _excluded = ["label", "feedback", "helper", "caption", "tooltip", "placeholder", "defaultPlaceholder", "enabledCountries", "showPatternInErrorMessage", "generateValidationMessage", "errorMessage"];
var enabledCountries = getSupportedCountries();

// This functions purpose is to remove the `+XXX` country code if present and its not supposed to
// Example user pastes or browser autofills "+32470123456" in Belgium we want to force that to be "470123456"
var fixValue = function fixValue(value, country) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var formatData = getFormats(country);
  var v = "".concat(value);

  // Determine if we are including country code
  var includeCountryCode = options.format == 'E164' || options.format === 'international' || options.outOf;

  // If we are not supposed to include the country code and there is a "+" remove the + and country code
  if (!includeCountryCode && v && v.includes('+')) {
    v = v.slice(formatData.countryCode.length + 1, v.length);
  }
  return v;
};

/**
 * @param {*} value
 */
var parsePhone = function parsePhone(value, country) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var props = arguments.length > 3 ? arguments[3] : undefined;
  // console.log('V', value);

  var formatData = getFormats(country);
  var v = value != null && value !== '' ? String(value).replace(/\D/g, '') : undefined;

  // console.log('OPS', options);

  // If user pased national list
  var nationalFormat = props.formatNational && props.formatNational.includes(country) || options.format === 'national';

  // Example1: nddPrefix = 0  returns 0678 ---> 678
  // Example2: nddPrefix = 06 returns 0678 ---> 78
  if (nationalFormat && v && v.slice(0, formatData.nddPrefix.length) == formatData.nddPrefix) {
    v = v.slice(formatData.nddPrefix.length, v.length);
  }
  return v;
};

/**
 * Renders a phone input
 *
 */
function PhoneInput(props) {
  var _props$defaultValue, _props$initialValue2, _props$initialValue3, _props$initialValue4, _props$options;
  var _useCountriesList = useCountriesList(),
    defaultCountry = _useCountriesList.defaultCountry,
    countries = _useCountriesList.countries;

  // Ref to phone input
  var numberRef = useRef();
  var defaultValue = (_props$defaultValue = props.defaultValue) !== null && _props$defaultValue !== void 0 ? _props$defaultValue : {
    country: defaultCountry.code,
    code: defaultCountry.phone
  };
  var formApi = useFormApi();

  /**
   * Build validation function
   *
   * @param {PhoneValue} value
   * */
  var validate = function validate(value, values) {
    if (value) {
      if (!getPatterns(value.country)) {
        if (props.generateValidationMessage) {
          return props.generateValidationMessage(_objectSpread2({}, value));
        }
        return 'Unknown country.';
      }

      // Get the pattern based on country code
      // Brackets are required for `^` to be applied to
      // all or-ed (`|`) parts, not just the first one.
      var pattern = "^(".concat(getPatterns(value.country).national, ")$");

      // We have value and it does not match the pattern
      if (value.number != null && !new RegExp(pattern).test(value.number)) {
        if (props.showPatternInErrorMessage) {
          return "Number must match ".concat(pattern);
        }
        if (props.generateValidationMessage) {
          return props.generateValidationMessage(_objectSpread2({}, value));
        }
        if (props.errorMessage) {
          return props.errorMessage;
        }
        return 'Please enter a valid phone number.';
      }
    }
    if (props.required) {
      if ((value === null || value === void 0 ? void 0 : value.number) == null) {
        return typeof props.required === 'string' ? props.required : 'Please enter a phone number';
      }
    }
    if (props.validate) {
      return props.validate(value, values);
    }
  };
  var initVal = useMemo(function () {
    var _props$initialValue;
    var iv = (_props$initialValue = props.initialValue) !== null && _props$initialValue !== void 0 ? _props$initialValue : formApi.getInitialValue(props.name);
    if (iv) {
      var _v = _objectSpread2(_objectSpread2({}, iv), {}, {
        code: getCountryCode(iv.country)
      });

      // Might need to remove the example: `+32`
      if (_v.number != null) {
        _v.number = fixValue(_v.number, _v.country, props.options);
      }
      return _v;
    }
  }, [(_props$initialValue2 = props.initialValue) === null || _props$initialValue2 === void 0 ? void 0 : _props$initialValue2.number, (_props$initialValue3 = props.initialValue) === null || _props$initialValue3 === void 0 ? void 0 : _props$initialValue3.country, (_props$initialValue4 = props.initialValue) === null || _props$initialValue4 === void 0 ? void 0 : _props$initialValue4.code]);
  var defaultVal = useMemo(function () {
    if (defaultValue && defaultValue.code == null) {
      return _objectSpread2(_objectSpread2({}, defaultValue), {}, {
        code: getCountryCode(defaultValue.country)
      });
    }
    return defaultValue;
  }, []);
  var _useField = useField(_objectSpread2(_objectSpread2({}, props), {}, {
      initialValue: initVal,
      defaultValue: defaultVal,
      validate: validate,
      inputRef: numberRef,
      inputRefs: {
        number: numberRef
      },
      formatter: {
        number: function number(n, full) {
          var _getValue;
          // console.log('AHHH', n, phoneFormatter(parsePhone(n), getValue()?.country || initialValue.country));
          // For backwards compatability ( before we passed full )
          var country = full ? full === null || full === void 0 ? void 0 : full.country : (_getValue = getValue()) === null || _getValue === void 0 ? void 0 : _getValue.country;
          var opts = _objectSpread2({}, props.options);

          // If user pased national list
          var nationalFormat = props.formatNational && props.formatNational.includes(country);
          if (nationalFormat) {
            opts.format = 'national';
          }
          return phoneFormatter(parsePhone(n, country, props.options, props), country, opts); //|| initialValue.country);
        }
      },
      parser: {
        number: function number(n) {
          var _getValue2;
          return parsePhone(n, (_getValue2 = getValue()) === null || _getValue2 === void 0 ? void 0 : _getValue2.country, props.options, props);
        }
      },
      formatterDependencies: [(_props$options = props.options) === null || _props$options === void 0 ? void 0 : _props$options.format]
    })),
    fieldApi = _useField.fieldApi,
    fieldState = _useField.fieldState,
    render = _useField.render,
    userProps = _useField.userProps;
  var label = userProps.label,
    feedback = userProps.feedback,
    helper = userProps.helper,
    caption = userProps.caption,
    tooltip = userProps.tooltip,
    userPlaceholder = userProps.placeholder,
    _userProps$defaultPla = userProps.defaultPlaceholder,
    defaultPlaceholder = _userProps$defaultPla === void 0 ? 'mobile' : _userProps$defaultPla,
    userEnabledCountries = userProps.enabledCountries;
    userProps.showPatternInErrorMessage;
    userProps.generateValidationMessage;
    userProps.errorMessage;
    var inputProps = _objectWithoutProperties(userProps, _excluded);
  var disabled = inputProps.disabled,
    readonly = inputProps.readonly;
  var id = userProps.id;
  var showError = fieldState.showError,
    error = fieldState.error,
    value = fieldState.value,
    maskedValue = fieldState.maskedValue;
  var setValue = fieldApi.setValue,
    setTouched = fieldApi.setTouched,
    getValue = fieldApi.getValue;

  /** @type {PhoneValue} */
  var v = value || {};

  // Default placeholder
  var phoneExample = v.country ? getExample(v.country, defaultPlaceholder) : '';
  // @ts-ignore
  var exampleNumber = phoneExample;
  var placeholder = userPlaceholder;
  if (!placeholder && exampleNumber != '') {
    placeholder = utils.informedFormat(exampleNumber, phoneFormatter(exampleNumber, v.country)).value;
  } else if (!placeholder) {
    placeholder = '';
  }

  /**
   * Change handler for the country select tooltip
   *
   * @param {{ code: String, phone: String }} c
   */
  var tooltipChange = function tooltipChange(c) {
    var _props$options2;
    // Get code and phone out of the handler
    var code = c.code,
      phone = c.phone;

    // Next grab the code and prefix out of tooltip change
    var newVal = _objectSpread2({}, getValue());
    var formatData = getFormats(code);
    newVal.country = code;
    newVal.code = +phone;

    // If user pased national list
    var nationalFormat = props.formatNational && props.formatNational.includes(code);
    if (((_props$options2 = props.options) === null || _props$options2 === void 0 ? void 0 : _props$options2.format) === 'national' || nationalFormat) {
      newVal.nddPrefix = formatData.nddPrefix;
    }
    setValue(newVal);
  };

  /**
   * @param {string} number
   */
  var numberChange = function numberChange(e) {
    var newVal = _objectSpread2({}, getValue());
    newVal.number = e.target.value;

    // Remove the country code if it was pasted into the input field
    newVal.number = fixValue(newVal.number, newVal.country, props.options);
    setValue(newVal, e, 'number');
  };

  // Validate the userEnabled countries if they were passed
  useMemo(function () {
    if (userEnabledCountries) {
      userEnabledCountries.forEach(
      /**
       * @param {string} c
       */
      function (c) {
        if (!enabledCountries.some(function (e) {
          return e === c;
        })) {
          throw new Error("Country with code ".concat(c, " is not currently supported by this phone input :("));
        }
      });
    }
  }, []);

  // Build tooltip state from selected field state
  var tooltipState = useMemo(function () {
    return countries[value === null || value === void 0 ? void 0 : value.country];
  }, [value === null || value === void 0 ? void 0 : value.country]);

  // Use the users or the enabled
  var enabled = userEnabledCountries || enabledCountries;
  return render( /*#__PURE__*/React.createElement(FormItem, {
    error: showError,
    variant: "text",
    disabled: disabled
  }, label ? /*#__PURE__*/React.createElement(FormLabel, {
    htmlFor: id,
    caption: caption,
    tooltip: tooltip
  }, label) : null, /*#__PURE__*/React.createElement(FormInputText, _extends({
    inputMode: "tel",
    onBlur: function onBlur() {
      setTouched(true);
    }
  }, inputProps, {
    placeholder: placeholder,
    id: id,
    readOnly: readonly
    // Mask takes control
    ,
    inputRef: numberRef,
    value: (maskedValue === null || maskedValue === void 0 ? void 0 : maskedValue.number) || '',
    onChange: numberChange,
    leading: /*#__PURE__*/React.createElement(PhoneTooltip, {
      enabledCountries: enabled,
      country: tooltipState,
      onCountrySelect: tooltipChange
    }),
    disabled: disabled
  })), /*#__PURE__*/React.createElement(FeedBack, {
    feedback: feedback,
    helper: helper,
    error: error,
    showError: showError
  })));
}

export { PhoneInput };
